import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const TABS = [
  { id: "weekly",  label: "LAST 12 WEEKS",  dataKey: "weekly" },
  { id: "monthly", label: "LAST 12 MONTHS", dataKey: "monthly" },
];

const COLORS = {
  run:   "#FC4C02",  // Strava orange — running
  walk:  "#F59E0B",  // Amber — walking
  other: "#52525B",  // Zinc — other/cycle
};

export default function ProgressCharts({ userId }) {
  const [data, setData] = useState({ weekly: [], monthly: [] });
  const [tab, setTab] = useState("weekly");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/users/${userId}/progress`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const rows = data[tab] || [];
  const total = rows.reduce((s, r) => s + (r.total || 0), 0);
  const peak = Math.max(0, ...rows.map((r) => r.total || 0));
  const empty = total === 0;

  return (
    <section className="mt-12" data-testid="progress-charts">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">PROGRESS</p>
          <h2 className="font-display text-2xl text-white tracking-wider mt-1">Distance over time</h2>
        </div>
        <div className="hairline bg-zinc-900 inline-flex" data-testid="progress-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`progress-tab-${t.id}`}
              className={`px-4 py-2 font-display tracking-[0.25em] text-[11px] border-r border-zinc-800 last:border-r-0 transition ${
                tab === t.id ? "bg-orange-600 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="hairline bg-zinc-900 p-4 sm:p-5">
        {/* Summary strip */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Tile label={tab === "weekly" ? "12-WEEK KM" : "12-MONTH KM"} value={total.toFixed(1)} />
          <Tile label={tab === "weekly" ? "PEAK WEEK" : "PEAK MONTH"} value={peak.toFixed(1)} />
          <Tile
            label={tab === "weekly" ? "AVG / WEEK" : "AVG / MONTH"}
            value={(total / Math.max(1, rows.length)).toFixed(1)}
          />
        </div>

        {loading ? (
          <div className="h-[260px] flex items-center justify-center font-mono text-xs tracking-widest text-zinc-500 uppercase">
            Loading…
          </div>
        ) : empty ? (
          <div className="h-[260px] flex items-center justify-center font-mono text-xs tracking-widest text-zinc-500 uppercase text-center px-4">
            No {tab === "weekly" ? "weekly" : "monthly"} data yet — log a run to see progress.
          </div>
        ) : (
          <div className="h-[280px] -ml-2" data-testid={`chart-${tab}`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "JetBrains Mono, monospace", letterSpacing: 1 }}
                  axisLine={{ stroke: "#3f3f46" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  unit=" km"
                />
                <Tooltip
                  cursor={{ fill: "rgba(252, 76, 2, 0.08)" }}
                  contentStyle={{
                    background: "#09090b",
                    border: "1px solid #27272a",
                    borderRadius: 0,
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 11,
                    color: "#fafafa",
                  }}
                  labelStyle={{ color: "#fc4c02", letterSpacing: 2, textTransform: "uppercase" }}
                  formatter={(value, name) => [`${value} km`, name.toUpperCase()]}
                />
                <Legend
                  wrapperStyle={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 10,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                    color: "#a1a1aa",
                    paddingTop: 6,
                  }}
                  iconType="square"
                />
                <Bar dataKey="run"   stackId="km" fill={COLORS.run}   name="Run" />
                <Bar dataKey="walk"  stackId="km" fill={COLORS.walk}  name="Walk" />
                <Bar dataKey="other" stackId="km" fill={COLORS.other} name="Other" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </section>
  );
}

function Tile({ label, value }) {
  return (
    <div className="hairline bg-zinc-950 border-zinc-800 p-3">
      <div className="font-display text-2xl text-orange-500 leading-none">{value} <span className="text-xs text-zinc-500">KM</span></div>
      <div className="font-mono text-[9px] text-zinc-500 tracking-[0.25em] mt-1.5 uppercase">{label}</div>
    </div>
  );
}
